import { MouseEventHandler, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'components/Button';
import { Select } from 'components/select/Select';
import { SelectConfirmButton, SelectFooter } from 'components/select/SelectFooter';
import { useSelectClose } from 'components/select/SelectContext';
import { SelectItemRenderer, SelectOption } from 'components/select/types';
import { FlatSelectContent } from 'components/select/FlatSelectContent';

type FilterFunc<ItemType> = (
  option: SelectOption<ItemType>,
  searchTerm: string,
  keys: { label: string; description: string; value: string }
) => boolean;

function SelectMenuExtra({
  renderMenuBottomExtra,
  searchValue,
  minSearchCharacters,
  resultsCount,
}: {
  renderMenuBottomExtra: NonNullable<BaseSelectProps<string>['renderMenuBottomExtra']>;
  searchValue: string;
  minSearchCharacters: number;
  resultsCount: number;
}) {
  const close = useSelectClose();

  return (
    <div className="border-t border-snow-500 p-3">
      {renderMenuBottomExtra(searchValue, minSearchCharacters, resultsCount, close ?? (() => undefined))}
    </div>
  );
}

export interface BaseSelectProps<ItemType> {
  renderItem?: SelectItemRenderer<ItemType>;
  selectedItems: ItemType[];
  setSelectedItems: (items: ItemType[]) => void;
  allOptionItems: SelectOption<ItemType>[];
  label: string;
  className?: string;
  showConfirmButton?: boolean;
  disabled?: boolean;
  id?: string;
  onFilter?: (filteredCount: number, searchTerm?: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
  searchPlaceholder?: string;
  filterFunc?: FilterFunc<ItemType>;
  renderMenuBottomExtra?: (
    searchValue: string,
    minSearchCharacters: number,
    resultsCount: number,
    onClose: MouseEventHandler
  ) => React.ReactNode;
  minSearchCharacters?: number;
  hideDefaultButtons?: boolean;
}

export function BaseSelect<ItemType extends string>({
  renderItem = undefined,
  selectedItems,
  setSelectedItems,
  allOptionItems,
  label,
  className = undefined,
  showConfirmButton = false,
  disabled = false,
  id = '',
  onFilter = undefined,
  onOpen = undefined,
  onClose = undefined,
  searchPlaceholder = undefined,
  filterFunc = undefined,
  renderMenuBottomExtra = undefined,
  minSearchCharacters = 0,
  hideDefaultButtons = false,
}: BaseSelectProps<ItemType>) {
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState('');
  const selectedValues = useMemo(() => new Set(selectedItems), [selectedItems]);
  const labelsByValue = useMemo(
    () => new Map(allOptionItems.map(option => [option.value, option.label])),
    [allOptionItems]
  );

  const filteredOptions = useMemo(() => {
    const searchTerm = searchValue.trim().toLowerCase();
    if (!searchTerm) return allOptionItems;

    return allOptionItems.filter(option =>
      filterFunc
        ? filterFunc(option, searchTerm, { label: 'label', description: 'description', value: 'value' })
        : String(option.label ?? '')
            .toLowerCase()
            .includes(searchTerm)
    );
  }, [allOptionItems, filterFunc, searchValue]);

  const toggleItem = (value: ItemType) => {
    setSelectedItems(
      selectedValues.has(value) ? selectedItems.filter(item => item !== value) : [...selectedItems, value]
    );
  };

  const selectAll = () => {
    const values = filteredOptions
      .map(option => option.value)
      .filter((value): value is ItemType => value !== undefined);
    const visibleValues = new Set(values);
    const allSelected = values.length > 0 && values.every(value => selectedValues.has(value));
    setSelectedItems(
      allSelected
        ? selectedItems.filter(value => !visibleValues.has(value))
        : [...new Set([...selectedItems, ...values])]
    );
  };

  const allVisibleSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every(option => option.value !== undefined && selectedValues.has(option.value));

  const openMenu = () => {
    setSearchValue('');
    onOpen?.();
  };

  const displayValue = selectedItems
    .map(value => labelsByValue.get(value))
    .filter((value): value is string => Boolean(value))
    .join(', ');
  return (
    <div>
      <Select
        label={label}
        value={displayValue}
        selectedCount={selectedItems.length > 0 ? selectedItems.length : undefined}
        hasValue={selectedItems.length > 0}
        disabled={disabled}
        id={id}
        className={className}
        onOpen={openMenu}
        onClose={onClose}
        search={{
          value: searchValue,
          placeholder: searchPlaceholder ?? intl.formatMessage({ id: 'select.search', defaultMessage: 'Search' }),
          onChange: value => {
            setSearchValue(value);
            const searchTerm = value.trim().toLowerCase();
            const nextFilteredOptions = !searchTerm
              ? allOptionItems
              : allOptionItems.filter(option =>
                  filterFunc
                    ? filterFunc(option, searchTerm, { label: 'label', description: 'description', value: 'value' })
                    : String(option.label ?? '')
                        .toLowerCase()
                        .includes(searchTerm)
                );
            onFilter?.(nextFilteredOptions.length, value);
          },
        }}
        footer={
          <>
            {renderMenuBottomExtra && (
              <SelectMenuExtra
                renderMenuBottomExtra={renderMenuBottomExtra}
                searchValue={searchValue}
                minSearchCharacters={minSearchCharacters}
                resultsCount={filteredOptions.length}
              />
            )}
            {!hideDefaultButtons && (
              <SelectFooter>
                <Button priority="secondary" size="sm" onClick={selectAll} disabled={filteredOptions.length === 0}>
                  {allVisibleSelected ? (
                    <FormattedMessage id="select.clear-all" defaultMessage="Clear All" />
                  ) : (
                    <FormattedMessage id="select.select-all" defaultMessage="Select All" />
                  )}
                </Button>
                {showConfirmButton && (
                  <SelectConfirmButton>
                    <FormattedMessage id="select.confirm" defaultMessage="Confirm" />
                  </SelectConfirmButton>
                )}
              </SelectFooter>
            )}
          </>
        }
      >
        <FlatSelectContent
          options={filteredOptions}
          selectedItems={selectedItems}
          onToggle={toggleItem}
          renderItem={renderItem}
          selectId={id}
          emptyState={
            filteredOptions.length === 0 && searchValue.trim().length > 0 && !renderMenuBottomExtra ? (
              <div className="p-3 text-sm text-ash-500">
                <FormattedMessage id="async-select.search.no_results" defaultMessage="No matching results found." />
              </div>
            ) : undefined
          }
        />
      </Select>
    </div>
  );
}
